import { Navigate, createFileRoute } from '@tanstack/react-router';
import { Building, Check, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

import { Route as RootRoute } from '@/app/routes/_authenticated';
import { Route as RecipeRoute } from '@/app/routes/_authenticated/$tenantId/recipes';
import { Button } from '@/components/ui/button/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  useAcceptInvite,
  useDeleteInvite,
} from '@/features/team/api/team-mutations';
import { useUserInvites } from '@/features/team/api/team-queries';
import { supabaseClient } from '@/utils/supabase-client';

export function InvitesPage() {
  const navigate = Route.useNavigate();
  const { auth } = Route.useRouteContext();

  const { data: invites } = useUserInvites(auth.user.email);
  const deleteInvite = useDeleteInvite();
  const acceptInvite = useAcceptInvite();

  const handleAcceptInvite = async (
    inviteId: string,
    organisationId: string,
  ) => {
    try {
      await acceptInvite(inviteId);

      // Refresh the session to get new custom claims
      await supabaseClient.auth.refreshSession();

      // Navigate to the organisation
      await navigate({
        to: RecipeRoute.to,
        params: {
          tenantId: organisationId,
        },
      });
    } catch {
      toast.error('Failed to accept invitation. Please try again.');
    }
  };

  const handleRejectInvite = async (inviteId: string) => {
    try {
      await deleteInvite.mutateAsync({ id: inviteId });
    } catch {
      toast.error('Failed to reject invitation. Please try again.');
    }
  };

  if (invites.length === 0) {
    return <Navigate to={RootRoute.to} />;
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Your Invitations</CardTitle>
            <CardDescription>
              You have been invited to join the following organisations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium">
                          {invite.organisation_title}
                        </h3>
                        <span className="inline-flex items-center rounded-full bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground">
                          {invite.role}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            {new Date(invite.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectInvite(invite.id)}
                      className="text-destructive"
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() =>
                        handleAcceptInvite(invite.id, invite.organisation_id)
                      }
                    >
                      <Check className="h-4 w-4" />
                      Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/invites')({
  component: InvitesPage,
});
